import { addDays, addYears, getYear, isAfter, isBefore, set, setDate, startOfToday } from 'date-fns';
import { matchPath } from 'react-router';
import { createSelector } from 'reselect';

import { CHANNEL } from 'constants/ChatConstants';
import { CHECKLIST_ACCESSORY, CHECKLIST_WORK } from 'constants/ChecklistConstants';
import { APPOINTMENT, SELF_SERVICE } from 'constants/ExtensionConstants';
import { INFO_MEDIARIES_INTEGRATIONS } from 'constants/IntegrationConstants';
import { MODE_ENTITY, REMINDER_TIRE_TYPES } from 'constants/InterventionConstants';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { SALES, TECHNICIAN } from 'constants/ModeConstants';
import { PAYMENT_GATEWAY } from 'constants/PaymentConstants';
import { EXTENSIONS, FEATURES } from 'constants/RightsConstants';
import { PATH } from 'constants/RoutesConstants';
import { CREATABLE_SELF_SERVICE_TYPES, SELF_SERVICE_ORIGIN, SELF_SERVICE_TYPE } from 'constants/SelfServiceConstants';

import { getAllBrands } from 'modules/brands/selectors';
import { getCountryByCode, getTimeZoneByIANATimeZone } from 'modules/countries/selectors';
import { DEFAULT_DISTANCE_UNIT } from 'modules/dealers/constants';
import {
  AutomaticDocumentScannerProviderEnum,
  IntegrationTypeEnum,
} from 'modules/global/typing/enums/integrations.enum';
import { getSelectedGroup, getSelectedGroupId } from 'modules/groups/selectors';
import { isSelfServiceOnly } from 'modules/selfServices/utils';

import {
  getUserAccessMatrix,
  getCurrentUserGroupId,
  getCurrentUserIsAdmin,
  getCurrentUserDealerIds,
  getCurrentUserIsReadOnly,
  getCurrentUserIsGroupManager,
} from 'modules/users/selectors/currentUser';
import {
  getDealerLanguage,
  canPlanIntervention,
  hasSelfServiceTypesOrigins,
  getValueAddedTax as getDealerVAT,
} from 'services/DealerUtils';
import { getDayInMonth, groupBy } from 'utils/global';
import { pickByIds, uniq } from 'utils/ObjectUtils';
import { getProps, getSecondProps } from 'utils/selectors';

import { getCurrentLocation } from './router';

/**
 * @param {{ dealers : import('modules/dealers/types').State }} state
 */
const getState = state => state.dealers;

const getId = (_, dealerId) => dealerId;

const getPath = (_, __, entity) => entity;

const getRoot = root => root;

const getDealerUI = createSelector(getState, state => state.ui);

export const getEntities = createSelector(getState, state => state.entities);

export const getDealersEntitiesById = createSelector(getEntities, entities => entities.byId ?? {});

export const getSelectedDealerId = createSelector(getEntities, entities => entities.selectedId);

/**
 * Get dealer ID from URL params (for administration details page)
 * Returns 'new' for creation or the actual dealerId for edition
 */
export const getDealerIdFromUrl = createSelector(getCurrentLocation, location => {
  const match = matchPath(PATH.DEALERS_DETAILS.path, location);
  return match?.params?.dealerId;
});

export const getDealersByGroupId = createSelector(getDealersEntitiesById, getId, (dealers, groupId) =>
  Object.values(dealers).filter(dealer => !groupId || dealer?.groupId === groupId));

export const getDealerById = createSelector(getDealersEntitiesById, getId, (all, id) => all?.[id]);

export const getDealersByIds = createSelector(getDealersEntitiesById, getId, (all = [], ids = []) =>
  ids.map(id => all[id]).filter(Boolean));

/**
 * @type {(state: any, dealerIds: string[]) => Record<string, import('modules/brands/types').Brand>}
 */
export const getBrandsByDealerIds = createSelector(
  getDealersEntitiesById,
  getAllBrands,
  getId,
  (dealersById, brands, dealerIds = []) => {
    const brandIds = dealerIds.reduce((acc, dealerId) => {
      acc.push(...(dealersById[dealerId]?.brandIds ?? []));
      return acc;
    }, []);

    return pickByIds(brandIds, brands);
  }
);

export const getDealersBySelectedGroupId = createSelector(getRoot, getSelectedGroupId, getDealersByGroupId);

export const getDealersIsFetching = createSelector(getDealerUI, ui => ui.fetching);

export const getDealersApiError = createSelector(getDealerUI, ui => ui.apiError);

export const getDealersIsUpdateDealer = createSelector(getDealerUI, ui => ui.updating);

export const getDealerNameCheckError = createSelector(getDealerUI, ui => ui?.dealerName?.error);

export const getDealerNameCheckValidating = createSelector(getDealerUI, ui => ui?.dealerName?.isValidating);

export const getSelectedDealer = createSelector(
  getSelectedDealerId,
  getDealersEntitiesById,
  (dealerId, dealers) => dealers?.[dealerId]
);

export const getSelectedDealerShortname = createSelector(getSelectedDealer, dealer => {
  const defaultContact = dealer?.config?.general?.contacts?.find(contact => typeof contact.brandId === 'undefined');
  return defaultContact?.shortName;
});

const getCountryCodeByDealerId = createSelector(getDealerById, dealer => dealer?.countryCode ?? dealer?.country?.code);

export const getCountryByDealerId = createSelector(getRoot, getCountryCodeByDealerId, getCountryByCode);
const getCountry = createSelector(getRoot, getSelectedDealerId, getCountryByDealerId);

export const getCurrencyByDealerId = createSelector(getCountryByDealerId, country => country?.currency);
export const getSelectedDealerCurrency = createSelector(getCountry, country => country?.currency);
export const getDealerCurrencyIsoCode = createSelector(getCountryByDealerId, country => country?.currencyIsoCode);

const getSelectedDealerCountryCode = createSelector(getCountry, country => country?.code);

export const getCurrencyIsoCode = createSelector(getCountry, country => country?.currencyIsoCode);

export const getSelectedDealerCategories = createSelector(
  getSelectedGroup,
  getSelectedDealer,
  getId,
  getPath,
  (group, dealer, mode = TECHNICIAN, checklistType = CHECKLIST_WORK) => {
    const categories =
      dealer?.group?.checklistAdviceCategories?.[checklistType] ??
      dealer?.group?.checklistAdviceCategories?.[mode] ??
      group?.checklistAdviceCategories?.[checklistType] ??
      group?.checklistAdviceCategories?.[mode] ??
      [];

    // Special case
    if (mode === SALES && checklistType === CHECKLIST_ACCESSORY) {
      const accessoryCategories = ['ACCESSORY', 'SERVICE', 'OPTION', 'OTHER'];
      return accessoryCategories.filter(category => categories?.includes(category));
    }

    return categories;
  }
);

export const getSelectedDealerExtensions = createSelector(getSelectedDealer, dealer => dealer?.extensions ?? []);

export const getFeaturesByDealerId = createSelector(getDealerById, dealer => dealer?.features ?? []);
export const hasFeatureByDealerId = createSelector(getFeaturesByDealerId, getSecondProps, (dealerFeatures, feature) => (
  dealerFeatures.includes(feature) ?? false));
export const hasFeaturesByDealerId = createSelector(getFeaturesByDealerId, getSecondProps, (dealerFeatures, features) => (
  features.every(feature => dealerFeatures.includes(feature))));

export const getSelectedDealerFeatures = createSelector(getSelectedDealer, dealer => dealer?.features ?? []);
export const selectedDealerHasFeature = createSelector(getSelectedDealerFeatures, getProps, (dealerFeatures, feature) => (
  dealerFeatures.includes(feature) ?? false));

export const getDealerAccessMatrix = createSelector(
  getUserAccessMatrix,
  getSelectedDealerExtensions,
  getSelectedDealerFeatures,
  (userRightsMatrix, extensions = [], features = []) => ({
    ...userRightsMatrix,
    [EXTENSIONS]: extensions,
    [FEATURES]: features,
  })
);

export const getLanguageByDealerId = createSelector(
  getId,
  getDealersEntitiesById,
  (dealerId, dealers) => getDealerLanguage(dealers?.[dealerId]) ?? DEFAULT_LANGUAGE
);

export const getDealerDefaultLanguage = createSelector(getRoot, getSelectedDealerId, getLanguageByDealerId);

export const getTimeZoneByDealerId = createSelector(
  getDealerById,
  dealer => dealer?.timezone ?? dealer?.config?.general.timezone
);

export const getSelectedDealerTimeZone = createSelector(getRoot, getSelectedDealerId, getTimeZoneByDealerId);

export const getTimeZoneOffsetByDealerId = createSelector(getRoot, getTimeZoneByDealerId, getTimeZoneByIANATimeZone);

export const getSelectedDealerTimeZoneOffset = createSelector(
  getRoot,
  getSelectedDealerTimeZone,
  getTimeZoneByIANATimeZone
);

export const getGroupIdByDealerId = createSelector(getDealerById, dealer => dealer?.groupId ?? dealer?.group?.id);

export const getValueAddedTax = createSelector(getSelectedDealer, getDealerVAT);

export const getValueAddedTaxByDealerId = createSelector(getDealerById, getDealerVAT);

export const getDealerConfig = createSelector(getDealerById, dealer => dealer?.config);

export const getDealerIdExtensions = createSelector(getDealerById, dealer => dealer?.extensions ?? []);

export const getRemindersDealerConfig = createSelector(
  getDealerById,
  getSecondProps,
  /** @return {import('modules/dealers/_types/interfaces/config-after-sales').ReminderConfig} */
  (dealer, mode = TECHNICIAN) => {
    const field = MODE_ENTITY[mode] ?? MODE_ENTITY[TECHNICIAN];
    return dealer?.config?.[field]?.reminder ?? {};
  }
);

export const hasExtensions = createSelector(
  getDealerById,
  getPath,
  (dealer, extension) => dealer?.extensions?.includes(extension) ?? false
);

export const hasOditooPack = createSelector(getDealerById, getPath, (dealer, pack) => dealer?.config?.pack?.oditooPack === pack);

export const getDealersById = createSelector(
  getId,
  getDealersEntitiesById,
  (/** @type {Array<string>} */ ids, dealers) => ids.map(dealerId => dealers?.[dealerId])
);

export const hasPlannedInterventions = createSelector(getDealerById, canPlanIntervention);

export const currentDealerHaveIntegration = createSelector(
  getSelectedDealer,
  getId,
  (dealer, /** @type {import('modules/global/typing/enums/integrations.enum').IntegrationTypeEnum} */ integration) =>
    dealer?.config?.integration?.integrations?.includes(integration)
);

const automatedDocumentValidationProvider = createSelector(
  getSelectedDealer,
  (dealer) => dealer?.config?.integration?.automatedAction?.documentValidation?.provider
);

export const hasPxlVisionDocumentValidation = createSelector(
  automatedDocumentValidationProvider,
  state => selectedDealerHasFeature(state, IntegrationTypeEnum.AUTOMATED_DOCUMENT_VALIDATION),
  (provider, integrationValid) => provider === AutomaticDocumentScannerProviderEnum.PXL_VISION && integrationValid
);

export const getAutomatedDocumentWorkflow = createSelector(
  getSelectedDealer,
  dealer => dealer?.contractProperties?.automatedDocumentValidation?.selectedWorkflow
);

export const canDisabledSteps = createSelector(
  getSelectedDealer,
  getId,
  getCurrentUserIsReadOnly,
  (dealer, mode, readonly) => {
    const INTEGRATION_STEPS = [IntegrationTypeEnum.ISPA, IntegrationTypeEnum.SOFT_NRG];
    const hasIntegration =
      dealer?.config?.integration?.integrations?.some(integration => INTEGRATION_STEPS.includes(integration)) ?? false;

    return !readonly && (mode !== TECHNICIAN || !hasIntegration);
  }
);

export const hasAccessToDealer = createSelector(
  getId,
  getDealerById,
  getCurrentUserDealerIds,
  getCurrentUserGroupId,
  getCurrentUserIsGroupManager,
  getCurrentUserIsAdmin,
  (dealerId, dealer, userDealerIds, userGroupId, isGroupManager, isAdmin) => {
    if (isAdmin) {
      return true;
    }
    if (isGroupManager && (dealer?.groupId ?? dealer?.group?.id) === userGroupId) {
      return true;
    }
    return userDealerIds?.includes(dealerId);
  }
);

export const getDefaultOfferValidity = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.sales?.defaultOfferValidity
);

export const isSelectedDealerAllowURL = createSelector(
  getSelectedDealerCountryCode,
  dealerCountryCode => !['no', 'se'].includes(dealerCountryCode)
);

export const isDealerAllowingReminderByModeAndId = createSelector(
  getDealerById,
  getPath,
  (dealer, mode) => dealer?.config?.[MODE_ENTITY[mode]]?.reminder?.allowReminder ?? false
);

const getDealerReminderDelayByModeAndId = createSelector(
  getDealerById,
  getPath,
  (dealer, mode) => dealer?.config?.[MODE_ENTITY[mode]]?.reminder?.daysBeforeSendReminder ?? 1
);

/**
 * @param {{ month: number, day: number }} config
 * @returns {Date}
 */
const generateSendDate = ({ month, day }) => {
  const date = Math.min(day, getDayInMonth(month, getYear(new Date())));

  let sendDate = set(new Date(), { month, date });
  if (isBefore(sendDate, new Date())) {
    sendDate = addYears(sendDate, 1);
    const newDate = Math.min(day, getDayInMonth(month, getYear(sendDate)));
    sendDate = setDate(sendDate, newDate);
  }

  return sendDate;
};

export const getDealerReminderSendDateByModeAndId = createSelector(
  getDealerById,
  getDealerReminderDelayByModeAndId,
  getPath,
  (dealer, delay, mode) => {
    const defaultDate = addDays(new Date(), delay);
    const config = dealer?.config?.[MODE_ENTITY[mode]];

    return (
      REMINDER_TIRE_TYPES.reduce((acc, type) => {
        const sendDateConfig = config?.tire[type]?.reminder?.sendDate;
        if (sendDateConfig) {
          const sendDate = generateSendDate(sendDateConfig);
          if (!acc || (isAfter(sendDate, startOfToday()) && isBefore(sendDate, acc))) {
            return sendDate;
          }
        }
        return acc;
      }, undefined) ?? defaultDate
    );
  }
);

export const isSelectedDealerAllowReminder = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.afterSales?.reminder?.allowReminder ?? dealer?.allowReminder ?? false
);

export const getSelectedDealerIntegrations = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.integration?.integrations ?? []
);

export const getSelectedDealerHasSalesClipping = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.sales?.clipping?.enabled ?? false
);

export const getSelectedDealerSMSProvider = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.integration?.smsProvider?.type
);

export const getDealerPrefix = createSelector(
  getDealerById,
  getCountryByDealerId,
  (dealer, country) => dealer?.country?.prefix ?? country?.prefix
);

const getPredefinedMessagesCollection = createSelector(getSelectedDealer, ({ config } = {}) => [
  ...(config?.sales?.selectedPredefinedMessages ?? []),
  ...(config?.afterSales?.selectedPredefinedMessages ?? []),
  ...(config?.keyDeposit?.selectedPredefinedMessages ?? []).map(msg => ({ ...msg, module: 'APPOINTMENT' })),
  ...(config?.audit?.selectedPredefinedMessages ?? []).map(msg => ({ ...msg, module: 'AUDIT' })),
]);

export const getPredefinedMessagesAttachments = createSelector(
  getPredefinedMessagesCollection,
  getId,
  (collection, messageId) =>
    groupBy(
      collection.filter(item => item.messageId === messageId),
      'module'
    )
);

export const getVAT = createSelector(getSelectedDealer, ({ config = {} }) => config.general?.valueAddedTax);

export const getDealerIntegration = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.integration?.integrations
);

export const getInfoMediariesIntegration = createSelector(getDealerIntegration, (integrations = []) =>
  INFO_MEDIARIES_INTEGRATIONS.filter(integration => integrations.includes(integration)));

export const isEnable360VideoByDealerId = createSelector(
  getDealerConfig,
  config => config?.sales?.enable360Video ?? false
);

export const isEnable360Video = createSelector(getRoot, getSelectedDealerId, isEnable360VideoByDealerId);

export const isEnable360InteriorByDealerId = createSelector(
  getDealerConfig,
  config => config?.sales?.enableImageInside360 ?? false
);

export const isEnable360Interior = createSelector(getRoot, getSelectedDealerId, isEnable360InteriorByDealerId);

const getSelectedDealerPack = createSelector(getSelectedDealer, dealer => dealer.config?.pack);

export const getSelectedDealerSelfServicePack = createSelector(
  getSelectedDealerPack,
  (pack = {}) => pack.selfServicePack
);

export const hasKioskCheckoutEnabled = createSelector(
  getDealerConfig,
  config =>
    config?.selfService?.availableTypes?.[SELF_SERVICE_TYPE.CHECK_OUT]?.includes(SELF_SERVICE_ORIGIN.KIOSK) ?? false
);

export const hasSelfServiceByDealerId = createSelector(
  getDealerById,
  dealer => dealer?.extensions?.includes(SELF_SERVICE) ?? false
);

export const hasAppointmentByDealerId = createSelector(
  getDealerById,
  dealer => dealer?.extensions?.includes(APPOINTMENT) ?? false
);

export const getAvailableSelfServiceTypesByDealerId = createSelector(
  hasSelfServiceByDealerId,
  getDealerById,
  (isAuthorized, dealer) => {
    const availableTypes = dealer?.config?.selfService?.availableTypes ?? {};

    return isAuthorized
      ? Object.entries(availableTypes).reduce((acc, [type, origins]) => {
        if (CREATABLE_SELF_SERVICE_TYPES.includes(type) && origins?.length > 0) {
          acc.push(type);
        }
        return acc;
      }, [])
      : [];
  }
);

export const getSendableSelfServiceTypeByDealerId = createSelector(
  hasSelfServiceByDealerId,
  getDealerById,
  (isAuthorized, dealer) => {
    const availableTypes = dealer?.config?.selfService?.availableTypes ?? {};

    return isAuthorized
      ? Object.entries(availableTypes).reduce((acc, [selfServiceType, origins]) => {
        const hasHome = origins.includes(SELF_SERVICE_ORIGIN.HOME);
        const hasKiosk =
            selfServiceType === SELF_SERVICE_TYPE.CHECK_OUT && origins.includes(SELF_SERVICE_ORIGIN.KIOSK);

        if (hasHome || hasKiosk) {
          acc.push(selfServiceType);
        }
        return acc;
      }, [])
      : [];
  }
);

export const hasKioskCheckoutByDealerId = createSelector(
  hasSelfServiceByDealerId,
  getDealerById,
  (isAuthorized, dealer) =>
    isAuthorized &&
    hasSelfServiceTypesOrigins(dealer?.config?.selfService?.availableTypes, {
      [SELF_SERVICE_TYPE.CHECK_OUT]: [SELF_SERVICE_ORIGIN.KIOSK],
    })
);

export const hasKioskCheckinByDealerId = createSelector(
  hasSelfServiceByDealerId,
  getDealerById,
  (isAuthorized, dealer) =>
    isAuthorized &&
    hasSelfServiceTypesOrigins(dealer?.config?.selfService?.availableTypes, {
      [SELF_SERVICE_TYPE.CHECK_IN]: [SELF_SERVICE_ORIGIN.KIOSK],
    })
);

export const hasKioskByDealerId = createSelector(
  hasKioskCheckinByDealerId,
  hasKioskCheckoutByDealerId,
  (hasCheckin, hasCheckout) => hasCheckin || hasCheckout
);

export const hasHomeCheckoutByDealerId = createSelector(
  hasSelfServiceByDealerId,
  getDealerById,
  (isAuthorized, dealer) =>
    isAuthorized &&
    hasSelfServiceTypesOrigins(dealer?.config?.selfService?.availableTypes, {
      [SELF_SERVICE_TYPE.CHECK_OUT]: [SELF_SERVICE_ORIGIN.HOME],
    })
);

export const checkSelfServiceKioskByDealerId = createSelector(
  hasKioskCheckoutByDealerId,
  hasHomeCheckoutByDealerId,
  (hasKiosk, hasHome) => hasKiosk && !hasHome
);

export const allowParkingSpotByDealerId = createSelector(
  getDealerById,
  dealer => dealer?.config?.afterSales?.parking?.allowParkingSpot
);

const hasParkingByDealerId = createSelector(
  getDealerById,
  dealer =>
    dealer?.config?.afterSales?.parking?.allowParkingSpot || dealer?.config?.afterSales?.parking?.maps?.length > 0
);

export const hasCheckOutParkingByDealerId = createSelector(
  getDealerById,
  hasParkingByDealerId,
  hasSelfServiceByDealerId,
  (dealer, hasParking, isAuthorized) =>
    hasParking &&
    isAuthorized &&
    hasSelfServiceTypesOrigins(dealer?.config?.selfService?.availableTypes, {
      [SELF_SERVICE_TYPE.CHECK_OUT]: [SELF_SERVICE_ORIGIN.ON_SITE, SELF_SERVICE_ORIGIN.KIOSK],
    })
);

export const currentDealerHasSelfServicePayment = createSelector(
  getRoot,
  getSelectedDealer,
  (state, dealer) =>
    Boolean(dealer) &&
    (dealer.config?.onlinePayment?.allowOnlinePayment || hasKioskCheckoutByDealerId(state, dealer.id))
);

export const getCurrentDealerFollowUpConfig = createSelector(getSelectedDealer, dealer => dealer?.config?.followUp);

const getFollowUpAutoSendConfigPerMode = createSelector(
  getCurrentDealerFollowUpConfig,
  getId,
  (followUpConfig, mode) => followUpConfig?.autoSend?.[mode]
);

export const getFollowUpAutoSendPeriodConfigPerMode = createSelector(
  getFollowUpAutoSendConfigPerMode,
  autoSendConfig => autoSendConfig?.period
);

export const hasMobilityEnabled = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.selfService?.mobility?.enabled
);

export const hasHideDownloadAction = createSelector(
  getSelectedDealer,
  dealer => Boolean(dealer?.config?.selfService?.mobility?.hideDownloadAction)
);

export const dealerHasBezahl = dealer =>
  Boolean(
    dealer?.config?.onlinePayment?.allowPaymentGateway &&
      dealer.config.onlinePayment.paymentGateway === PAYMENT_GATEWAY.BEZAHL_DE
  );

export const currentDealerHaveBezahlDe = createSelector(getSelectedDealer, dealerHasBezahl);

export const dealersHaveBezahl = createSelector(
  getDealersByIds,
  (dealers = []) => dealers.length > 0 && dealers.every(dealerHasBezahl)
);

export const dealerHaveOnlinePaymentOption = createSelector(
  getDealersByIds,
  (dealers = []) => dealers.length > 0 &&
    dealers.every(dealer => dealer.config?.onlinePayment?.allowOnlinePayment || dealer.config?.onlinePayment?.allowPaymentGateway)
);

export const extractMultipleChecklistIds = (state = {}, keys = ['ids', 'excludedIds']) => {
  const entries = Object.entries(state);
  return uniq(
    entries.reduce((acc, [field, value]) => {
      if (keys.includes(field)) {
        const tmp = !Array.isArray(value) ? [value] : value;
        return [...acc, ...tmp];
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        return [...acc, ...extractMultipleChecklistIds(value, keys)];
      }

      return acc;
    }, [])
  );
};

export const extractGroupChecklistIds = (state = {}) =>
  uniq(
    Object.values(state).reduce(
      (acc, checklistIdByBrandId) => [
        ...acc,
        ...(typeof checklistIdByBrandId === 'string' ? [checklistIdByBrandId] : Object.values(checklistIdByBrandId)),
      ],
      []
    )
  );

export const getConfigurationChecklistScope = createSelector(
  getSelectedDealer,
  getSelectedGroup,
  getProps,
  (dealer, group, field) => {
    const genericScope = [
    // Multiple checklists
      ...extractGroupChecklistIds(group?.config?.[field]?.selectedChecklists),
      ...extractMultipleChecklistIds(dealer?.config?.[field]?.selectedChecklists)
    ];

    if (field === 'afterSales') {
      return uniq([
        dealer?.config?.afterSales?.selectedOfferedWorksChecklist,
        dealer?.config?.afterSales?.selectedAuditDoneWorksChecklist,
        dealer?.config?.afterSales?.selectedAuditOfferedWorksChecklist,
        ...genericScope,
      ]);
    }
    return genericScope;
  }
);

export const isCreateRemindersFromSeverityById = createSelector(
  getDealerById,
  getSecondProps,
  (dealer, mode = TECHNICIAN) => {
    const field = MODE_ENTITY[mode];

    return (
      dealer?.config?.[field]?.reminder?.createRemindersFromSeverity ?? dealer?.createRemindersFromSeverity ?? false
    );
  }
);

export const isAllowSalesLeadById = createSelector(
  getDealerById,
  dealer => dealer?.config?.sales?.lead?.enableLead ?? dealer?.allowSalesLead ?? false
);

export const isAllowSalesLead = createSelector(getRoot, getSelectedDealerId, isAllowSalesLeadById);

export const getDaysBeforeSendSeverityHighById = createSelector(
  getRemindersDealerConfig,
  reminder => reminder?.daysBeforeSendSeverityHigh
);

export const getDaysBeforeSendSeverityLowById = createSelector(
  getRemindersDealerConfig,
  reminder => reminder?.daysBeforeSendSeverityLow
);

export const getRemindersDaysBeforeSendById = createSelector(
  getDealerById,
  getRemindersDealerConfig,
  (dealer, reminder) => /** @type {number} */ (reminder?.daysBeforeSendReminder ?? dealer?.daysBeforeSendReminder ?? 0)
);

export const getCustomerDefaultLanguageById = createSelector(
  getDealerById,
  dealer => dealer?.config?.general?.customerDefaultLanguage ?? dealer?.customerDefaultLanguage
);

export const getSelectedDealerGroupId = createSelector(
  getSelectedDealer,
  getSelectedGroupId,
  (dealer, groupId) => dealer?.group?.id ?? dealer?.groupId ?? groupId
);

export const getDealersExtensions = createSelector(getDealersByIds, dealers =>
  uniq(dealers.reduce((extensions, dealer) => [...extensions, ...(dealer.extensions ?? [])], [])));

export const selectedDealerHasOnlinePayment = createSelector(
  getSelectedDealer,
  dealer => dealer.config?.onlinePayment?.allowOnlinePayment
);

export const hasSummaryStatsByDealerIds = createSelector(getDealersByIds, dealers =>
  Object.values(dealers).some(({ config }) => config?.statistics?.activateSummaryStats));

export const getDealerUnits = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.general?.units ?? DEFAULT_DISTANCE_UNIT
);

export const getDealerUnitById = createSelector(getDealerById, dealer => dealer?.config?.general?.units);

export const getLargeDistanceUnit = createSelector(
  getDealerUnits,
  units => units?.largeDistanceUnit ?? DEFAULT_DISTANCE_UNIT.largeDistanceUnit
);

export const getSmallDistanceUnit = createSelector(
  getDealerUnits,
  units => units?.smallDistanceUnit ?? DEFAULT_DISTANCE_UNIT.smallDistanceUnit
);

export const getLargeDistanceUnitById = createSelector(
  getDealerUnitById,
  units => units?.largeDistanceUnit ?? DEFAULT_DISTANCE_UNIT.largeDistanceUnit
);

export const getSmallDistanceUnitById = createSelector(
  getDealerUnitById,
  units => units?.smallDistanceUnit ?? DEFAULT_DISTANCE_UNIT.smallDistanceUnit
);

const getDealer = createSelector(
  getDealerById,
  getSelectedDealer,
  (dealerById, selectedDealer) => dealerById ?? selectedDealer
);

export const dealerHasWhatsApp = createSelector(getDealer, dealer =>
  dealer?.config?.integration?.integrations?.includes(IntegrationTypeEnum.WHATSAPP));

export const getUnallowedChannels = createSelector(dealerHasWhatsApp, hasWhatsApp =>
  (hasWhatsApp ? [] : [CHANNEL.WHATSAPP]));

export const hasDealerSelfServiceOnly = createSelector(getSelectedDealer, isSelfServiceOnly);

export const isSelfServiceOnlyByDealerId = createSelector(getDealerById, isSelfServiceOnly);

export const getUseProactiveWorks = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.selfService?.useProactiveWorks
);

export const getSelectedDealerKeyloopDmsProviderId = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.integration?.keyloop?.dmsProviderId
);

export const isAutoSendCheckInPeriod = createSelector(
  getSelectedDealer,
  dealer => Boolean(dealer?.config?.selfService?.autoSendCheckInPeriod)
);

export const getHideTopContributorsTab = createSelector(
  getSelectedDealer,
  dealer => dealer?.config?.statistics?.hideTopContributorsTab
);

export const hasFeatures = createSelector(
  getDealerById,
  getPath,
  (dealer, feature) => dealer?.features?.includes(feature) ?? false
);

export const getGroupTagAssignmentIds = createSelector(
  getDealerById,
  getPath,
  (dealer, mode) => {
    const groupByMode = dealer?.group?.assignments?.tags?.[mode];
    if (!groupByMode) {
      return [];
    }

    return uniq(Object.values(groupByMode).flat().map(({ id }) => id));
  }
);

export const getGroupTagAssignmentsByDealerId = createSelector(
  getDealerById,
  dealer => dealer?.group?.assignments?.tags ?? {}
);

/**
 * @returns {string[] | undefined}
 */
export const getSelectedDealerFoTemplateIdsByDealerId = createSelector(
  getDealerById,
  dealer => {
    const ids = dealer?.config?.template?.selectedFoTemplates?.map(({ templateId }) => templateId);
    return ids && uniq(ids);
  }
);

/**
 * @returns {string[] | undefined}
 */
export const getSelectedDealerFoTemplateIds = createSelector(
  getRoot,
  getSelectedDealerId,
  getSelectedDealerFoTemplateIdsByDealerId
);
